import { getMessage } from '@tcc/i18n';
import { CropInnerCardAttributes } from './types/CropInnerCardAttributes';
import {
  localizeUnits
} from '../../../utils/processUnits';
import { LocaleGroup } from '../../../types';

interface GetLocalizedValueParams {
  isFieldYieldAnalysisAvailable: CropInnerCardAttributes['isFieldYieldAnalysisAvailable'];
  harvestData: CropInnerCardAttributes['harvestData'];
  cropNominalWeight?: CropInnerCardAttributes['cropNominalWeight'];
  cropCode: CropInnerCardAttributes['cropCode'];
  translationKey: string;
}
export interface LocalizedStat {
  label: string;
  value: string;
  testId: string;
}

const getUnitLabel = (unit?: string) => unit ? ` (${unit})` : '';

export const getLocalizedAverageYield = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropNominalWeight,
  cropCode,
  harvestData: {
    avgYield
  }
}: GetLocalizedValueParams): LocalizedStat => {
  const yieldLabelPrefix = getMessage(translationKey);
  const testId = 'harvest-avg-yield';
  
  if(!isFieldYieldAnalysisAvailable || !avgYield) {
    return {
      label: `${yieldLabelPrefix}${getUnitLabel(avgYield?.u)}`,
      value: '--',
      testId,
    };
  }

  const localizedYieldQU = localizeUnits(
    avgYield.q,
    { key: avgYield.u, localeGroup: LocaleGroup.YIELD, cropNominalWeight },
    { cropCode }
  );

  return {
    label: `${yieldLabelPrefix}${getUnitLabel(localizedYieldQU.u)}`,
    value: localizedYieldQU.q,
    testId,
  }
}

export const getLocalizedArea = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropNominalWeight,
  cropCode,
  harvestData: {
    area
  }
}: GetLocalizedValueParams): LocalizedStat => {
  const yieldLabelPrefix = getMessage(translationKey);
  const testId = 'harvest-area-harvested';
  
  if(!isFieldYieldAnalysisAvailable || !area) {
    return {
      label: `${yieldLabelPrefix}${getUnitLabel(area?.u)}`,
      value: '--',
      testId,
    };
  }

  const localizedYieldQU = localizeUnits(
    area.q,
    { key: area.u, localeGroup: LocaleGroup.AREA, cropNominalWeight },
    { cropCode }
  );

  return {
    label: `${yieldLabelPrefix}${getUnitLabel(localizedYieldQU.u)}`,
    value: localizedYieldQU.q,
    testId,
  }
}

export interface GetLocalizedValueParamsAsDryMatter extends GetLocalizedValueParams {
  dryMatter?: boolean
}

export const getLocalizedAverageMoisture = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropNominalWeight,
  cropCode,
  harvestData: {
    avgMoisture
  },
  dryMatter = false
}: GetLocalizedValueParamsAsDryMatter): LocalizedStat => {
  const yieldLabelPrefix = getMessage(translationKey);
  const testId = dryMatter ? 'harvest-dry-matter' : 'harvest-moisture';
  
  if(!isFieldYieldAnalysisAvailable || !avgMoisture) {
    return {
      label: yieldLabelPrefix,
      value: '--',
      testId,
    };
  }

  const localizedYieldQU = localizeUnits(
    dryMatter ? 100 - avgMoisture.q : avgMoisture.q,
    { key: avgMoisture.u, localeGroup: LocaleGroup.MOISTURE, cropNominalWeight },
    { cropCode }
  );

  return {
    label: yieldLabelPrefix,
    value: `${localizedYieldQU.q}${localizedYieldQU.u}`,
    testId,
  }
}

export const getLocalizedTotalWeight = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropNominalWeight,
  cropCode,
  harvestData: {
    totalYield
  }
}: GetLocalizedValueParams): LocalizedStat => {
  const yieldLabelPrefix = getMessage(translationKey);
  const testId = 'harvest-total-weight';
  
  if(!isFieldYieldAnalysisAvailable || !totalYield) {
    return {
      label: `${yieldLabelPrefix}${getUnitLabel(totalYield?.u)}`,
      value: '--',
      testId,
    };
  }
  const localizedYieldQU = localizeUnits(
    totalYield.q,
    { key: totalYield.u, localeGroup: LocaleGroup.WEIGHT, cropNominalWeight },
    { cropCode }
  );

  return {
    label: `${yieldLabelPrefix}${getUnitLabel(localizedYieldQU.u)}`,
    value: `${localizedYieldQU.q}`,
    testId,
  }
}

export const getLocalizedWetMassForSilage = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropNominalWeight,
  cropCode,
  harvestData: {
    wetMass
  }
}: GetLocalizedValueParams): LocalizedStat => {
  const yieldLabelPrefix = getMessage(translationKey);
  const testId = 'harvest-wet-mass';
  
  if(!isFieldYieldAnalysisAvailable || !wetMass) {
    return {
      label: `${yieldLabelPrefix}${getUnitLabel(wetMass?.u)}`,
      value: '--',
      testId,
    };
  }

  const localizedWetMassQU = localizeUnits(
    wetMass.q,
    { key: wetMass.u, localeGroup: LocaleGroup.WEIGHT_SILAGE, cropNominalWeight },
    { cropCode }
  );

  return {
    label: `${yieldLabelPrefix}${getUnitLabel(localizedWetMassQU.u)}`,
    value: `${localizedWetMassQU.q}`,
    testId,
  }
}

export const getLocalizedAverageLint = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropNominalWeight,
  cropCode,
  harvestData: {
    avgYield
  }
}: GetLocalizedValueParams): LocalizedStat => {
  const yieldLabelPrefix = getMessage(translationKey);
  const testId = 'harvest-avg-lint';
  
  if(!isFieldYieldAnalysisAvailable || !avgYield) {
    return {
      label: `${yieldLabelPrefix}${getUnitLabel(avgYield?.u)}`,
      value: '--',
      testId,
    };
  }

  const localizedYieldQU = localizeUnits(
    avgYield.q,
    { key: avgYield.u, localeGroup: LocaleGroup.YIELD_COTTON, cropNominalWeight },
    { cropCode }
  );

  return {
    label: `${yieldLabelPrefix}${getUnitLabel(localizedYieldQU.u)}`,
    value: localizedYieldQU.q,
    testId,
  }
};

export const getLocalizedTotalLint = (params: GetLocalizedValueParams): LocalizedStat => ({
  ...getLocalizedTotalWeight(params),
  testId: 'harvest-total-lint',
});

export const getLocalizedDryYieldForSilage = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropCode,
  harvestData: {
    avgDryYield
  }
}: GetLocalizedValueParams): LocalizedStat => {
  const yieldLabelPrefix = getMessage(translationKey);
  const testId = 'harvest-avg-dry-yield';
  
  if(!isFieldYieldAnalysisAvailable || !avgDryYield) {
    return {
      label: `${yieldLabelPrefix}${getUnitLabel(avgDryYield?.u)}`,
      value: '--',
      testId,
    };
  }

  const localizedYieldQU = localizeUnits(
    avgDryYield.q,
    { key: avgDryYield.u, localeGroup: LocaleGroup.YIELD_SILAGE },
    { cropCode }
  );

  return {
    label: `${yieldLabelPrefix}${getUnitLabel(localizedYieldQU.u)}`,
    value: localizedYieldQU.q,
    testId,
  }
};


export const getLocalizedDryMassForSilage = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropNominalWeight,
  cropCode,
  harvestData
}: GetLocalizedValueParams): LocalizedStat => {
  const dryMassLabelPrefix = getMessage(translationKey);
  const testId = 'harvest-dry-mass';
  const { dryMass } = harvestData;

  if(!isFieldYieldAnalysisAvailable || !dryMass) {
    return {
      label: `${dryMassLabelPrefix}${getUnitLabel(dryMass?.u)}`,
      value: '--',
      testId,
    };
  }

  const localizedDryMassQU = localizeUnits(
    dryMass.q,
    { key: dryMass.u, localeGroup: LocaleGroup.WEIGHT_SILAGE, cropNominalWeight },
    { cropCode }
  );

  return {
    label: `${dryMassLabelPrefix}${getUnitLabel(localizedDryMassQU.u)}`,
    value: `${localizedDryMassQU.q}`,
    testId,
  }
}
export const getLocalizedStdYieldForSilage = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropCode,
  harvestData: {
    standardisedAverageYield
  }
}: GetLocalizedValueParams): LocalizedStat => {
  const yieldLabelPrefix = getMessage(translationKey);
  const testId = 'harvest-std-yield';
  if(!isFieldYieldAnalysisAvailable || !standardisedAverageYield) {
    return {
      label: `${yieldLabelPrefix}${getUnitLabel(standardisedAverageYield?.u)}`,
      value: '--',
      testId,
    };
  }

  const localizedYieldQU = localizeUnits(
    standardisedAverageYield.q,
    { key: standardisedAverageYield.u, localeGroup: LocaleGroup.YIELD_SILAGE },
    { cropCode }
  );

  return {
    label: `${yieldLabelPrefix}${getUnitLabel(localizedYieldQU.u)}`,
    value: localizedYieldQU.q,
    testId,
  }
};

export const getLocalizedWetYieldForSilage = ({
  isFieldYieldAnalysisAvailable,
  translationKey,
  cropNominalWeight,
  cropCode,
  harvestData: {
    avgYield
  }
}: GetLocalizedValueParams): LocalizedStat => {
  const yieldLabelPrefix = getMessage(translationKey);
  const testId = 'harvest-avg-wet-yield';
  
  if(!isFieldYieldAnalysisAvailable || !avgYield) {
    return {
      label: `${yieldLabelPrefix}${getUnitLabel(avgYield?.u)}`,
      value: '--',
      testId,
    };
  }

  const localizedYieldQU = localizeUnits(
    avgYield.q,
    { key: avgYield.u, localeGroup: LocaleGroup.YIELD_SILAGE, cropNominalWeight },
    { cropCode }
  );

  return {
    label: `${yieldLabelPrefix}${getUnitLabel(localizedYieldQU.u)}`,
    value: localizedYieldQU.q,
    testId,
  }
};